import axios from "axios";
import { clearStorageExceptEventTokens } from "../utils/helper";

const $axios = axios.create({
  // DON'T CHANGE THIS and set REACT_APP_API_URL in .env file
  baseURL: process.env.REACT_APP_API_URL,
});

const doNotSendTokenList = [
  "/api/v1/user/register/",
  "/api/v1/user/confirm-registration/",
  "/api/v1/user/login/",
  "/api/v1/user/refresh/",
  "/api/v1/user/register/resend-otp/",
  "/api/v1/user/send-reset-password-otp/",
  "/api/v1/user/confirm-reset-password/",
  "/api/v1/user/set-new-password/",
  "/api/v1/event/login/",
  "/api/v1/event/refresh/",
];

$axios.interceptors.request.use(
  async (config) => {
    const isEventUrl = config.url?.includes("event");
    const token = isEventUrl
      ? localStorage.getItem("event_admin_access")
      : localStorage.getItem("X-Authorization");

    const language = localStorage.getItem("i18nextLng");
    if (language) {
      config.headers["Accept-Language"] = language;
    }

    if (!doNotSendTokenList.includes(config.url) && token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

let isUserRefreshing = false;
let userFailedQueue = [];

let isEventRefreshing = false;
let eventFailedQueue = [];

const processUserQueue = (error, token = null) => {
  userFailedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  userFailedQueue = [];
};

const processEventQueue = (error, token = null) => {
  eventFailedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  eventFailedQueue = [];
};

$axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !doNotSendTokenList.includes(originalRequest.url)
    ) {
      const isEventUrl = originalRequest.url?.includes("event");

      if (isEventUrl) {
        if (isEventRefreshing) {
          return new Promise((resolve, reject) => {
            eventFailedQueue.push({ resolve, reject });
          }).then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return $axios(originalRequest);
          });
        }

        originalRequest._retry = true;
        isEventRefreshing = true;

        const refreshToken = localStorage.getItem("event_admin_refresh");

        if (!refreshToken) {
          isEventRefreshing = false;
          localStorage.removeItem("event_admin_access");
          localStorage.removeItem("event_admin_refresh");
          window.location.href = "/";
          return Promise.reject(error);
        }

        try {
          const { data } = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v1/event/refresh/`,
            { refresh: refreshToken },
          );

          localStorage.setItem("event_admin_access", data.access);
          if (data.refresh) {
            localStorage.setItem("event_admin_refresh", data.refresh);
          }

          processEventQueue(null, data.access);
          originalRequest.headers["Authorization"] = `Bearer ${data.access}`;
          return $axios(originalRequest);
        } catch (refreshError) {
          processEventQueue(refreshError, null);
          localStorage.removeItem("event_admin_access");
          localStorage.removeItem("event_admin_refresh");
          window.location.href = "/";
          return Promise.reject(refreshError);
        } finally {
          isEventRefreshing = false;
        }
      } else {
        if (isUserRefreshing) {
          return new Promise((resolve, reject) => {
            userFailedQueue.push({ resolve, reject });
          }).then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return $axios(originalRequest);
          });
        }

        originalRequest._retry = true;
        isUserRefreshing = true;

        const refreshToken = localStorage.getItem("X-Refresh");

        if (!refreshToken) {
          isUserRefreshing = false;
          clearStorageExceptEventTokens();
          window.location.href = "/";
          return Promise.reject(error);
        }

        try {
          const { data } = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v1/user/refresh/`,
            { refresh: refreshToken },
          );

          localStorage.setItem("X-Authorization", data.access);
          if (data.refresh) {
            localStorage.setItem("X-Refresh", data.refresh);
          }
          localStorage.setItem(
            "tokenDate",
            JSON.stringify(new Date().toISOString()),
          );

          processUserQueue(null, data.access);
          originalRequest.headers["Authorization"] = `Bearer ${data.access}`;
          return $axios(originalRequest);
        } catch (refreshError) {
          processUserQueue(refreshError, null);
          clearStorageExceptEventTokens();
          window.location.href = "/";
          return Promise.reject(refreshError);
        } finally {
          isUserRefreshing = false;
        }
      }
    }

    return Promise.reject(error);
  },
);

export default $axios;
